// AI-CONTEXT: See docs/features/platform-mrr-card.md
import formatEuro from '../formatEuro'

// Het bedrag staat in centen (zelfde grondslag als Stripe); delen gebeurt hier,
// op het laatste moment.
const CENTS_PER_EURO = 100

const meervoud = (aantal, enkel, meer) => `${aantal} ${aantal === 1 ? enkel : meer}`

/**
 * Wat er NIET in het bedrag zit.
 *
 * Dit is geen versiering. 240 van de 245 klanten worden buiten Stripe
 * gefactureerd, dus het factuurdeel leunt volledig op handmatig ingevulde
 * bedragen. Zonder deze regel ziet "nog niet ingevuld" eruit als "betaalt
 * niets", en leest een vergeten verlenging als opzegging.
 *
 * Ontbrekende tellers zijn "onbekend", niet "nul" — een oud document dat die
 * velden nog niet draagt mag niet als volledig lezen.
 *
 * @param {object} current - PlatformRevenue/current
 * @returns {string|null}
 */
const buildNote = (current) => {
    const zonderBedrag = current.WithoutAmount
    const verlopen = current.Expired

    if (!Number.isFinite(zonderBedrag) || !Number.isFinite(verlopen)) {
        return 'aantallen onbekend'
    }

    const delen = []
    if (zonderBedrag > 0) delen.push(meervoud(zonderBedrag, 'organisatie zonder bedrag', 'organisaties zonder bedrag'))
    if (verlopen > 0) delen.push(meervoud(verlopen, 'verlopen periode', 'verlopen periodes'))

    // Een Stripe-abonnement zonder gekoppelde organisatie: het geld telt mee,
    // maar die organisatie staat op de abonnementenpagina als 'nog invullen' en
    // een ingevuld bedrag komt er bovenop. Een ontbrekend veld (ouder document)
    // is 'onbekend', niet 'nul' — daarom Number.isFinite en geen `|| 0`.
    const losseStripe = current.UnattributedStripe
    if (Number.isFinite(losseStripe) && losseStripe > 0) {
        delen.push(meervoud(losseStripe, 'Stripe-abonnement niet gekoppeld', 'Stripe-abonnementen niet gekoppeld'))
    }

    return delen.length ? delen.join(' · ') : null
}

/**
 * @typedef {object} MrrDisplay
 * @property {string|null} value - geformatteerd bedrag, of null zolang er niets bruikbaars is
 * @property {string|null} note - voorbehoud onder het bedrag
 */

/**
 * Label en bedrag voor de MRR-tegel.
 *
 * `value` is null zolang de cijfers niet geladen zijn of het veld ontbreekt —
 * de tegel toont dan een streepje. Nooit €0: dat is een bewering over de omzet
 * die op dat moment niet waar is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object|null} current - PlatformRevenue/current, of null
 * @returns {MrrDisplay}
 */
const mrrDisplay = (current) => {
    if (!current || !Number.isFinite(current.MrrCents)) {
        return { value: null, note: null }
    }

    return {
        value: formatEuro(current.MrrCents / CENTS_PER_EURO),
        note: buildNote(current),
    }
}

export default mrrDisplay
